import { createSelector } from 'reselect';
import { arrayToMap } from '../../utils/collections';

// function to compare buildings for ordering
function compareBuildingsByName(a, b) {
  const aName = a.name.toLowerCase();
  const bName = b.name.toLowerCase();
  if (aName < bName) return -1;
  if (aName > bName) return 1;
  return 0;
}

const getBuildingsOrderedByName = (state) =>
  Object.values(state.entities.buildings.byId).sort(compareBuildingsByName);

export const getBuildingField = (state, bid, field) => state.entities.buildings.byId[bid]?.[field];

export const getOrgsBuildings = (state, org) =>
  Object.values(state.entities.buildings.byId).filter((b) => !org || b.orgId === org);

export const filterBuildings = (
  buildingsById,
  { orgId: filterOrgId, buildings: buildingIds = [] }
) =>
  Object.values(buildingsById)
    .filter(({ orgId }) => filterOrgId == null || filterOrgId === orgId)
    .filter(({ id }) => !buildingIds.length || buildingIds.indexOf(id) !== -1)
    .map(({ id }) => id);

function idFn(d) {
  return d.id;
}

const getBuildingsDevices = createSelector(
  [
    (state) => state.entities.devices.byBuilding,
    (state) => state.entities.devices.byId,
    (state, buildingIds) => buildingIds,
  ],
  (idByBuilding, byId, buildingIds) =>
    buildingIds.flatMap((bid) => idByBuilding[bid] || []).map((did) => byId[did]),
  {}
);

const getBuildingsPoints = createSelector(
  [
    (state) => state.entities.points.byBuilding,
    (state) => state.entities.points.byId,
    (state, buildingIds) => buildingIds,
    (state) => state.entities.pointTypes.byId,
  ],
  (idByBuilding, pointsById, buildingIds, pointTypesById) =>
    buildingIds
      .flatMap((bid) => idByBuilding[bid] || [])
      .map((did) => {
        const point = pointsById[did];
        const pointType = pointTypesById[point.pointTypeId];
        return { tags: pointType?.tags, labels: pointType?.labels, ...point };
      }),
  {}
);

const getBuildingsEquipment = createSelector(
  [
    (state) => state.entities.equipment.byBuilding,
    (state) => state.entities.equipment.byId,
    (state, buildingIds) => buildingIds,
    (state) => state.entities.equipTypes.byId,
  ],
  (idByBuilding, equipsById, buildingIds, equipTypesById) =>
    buildingIds
      .flatMap((bid) => idByBuilding[bid] || [])
      .map((did) => {
        const equip = equipsById[did];
        const equipType = equipTypesById[equip.equipmentTypeId];
        return { tags: equipType?.tags, labels: equipType?.labels, ...equip };
      }),
  {}
);

export const getBuildingsEntities = createSelector(
  [getBuildingsDevices, getBuildingsPoints, getBuildingsEquipment],
  (devicesList, pointsList, equipmentList) => {
    const devices = arrayToMap(devicesList ?? [], idFn);
    const equipment = arrayToMap(
      (equipmentList ?? []).map((equip) => ({
        ...equip,
        points: [],
      })),
      idFn
    );
    const points = arrayToMap(
      (pointsList ?? []).map((point) => {
        const device = devices.get(point.publishedDeviceId);
        const newPoint = {
          ...point,
          equipment: point.equipmentIds.map((eid) => equipment.get(eid)).filter(Boolean),
          device,
        };
        newPoint.equipment.forEach((equip) => {
          equip.points.push(newPoint);
        });
        return newPoint;
      }),
      idFn
    );

    return { devices, points, equipment };
  }
);

export default getBuildingsOrderedByName;
